import { EventAggregator } from 'aurelia-event-aggregator';
import { Router } from 'aurelia-router';
import { autoinject } from 'aurelia-framework';
import * as moment from 'moment';
import numeral from 'numeral';
import { State } from '../../services/global-state';
import { I18N } from 'aurelia-i18n';
import { PortalTemplateTypes } from '../../services/template-service';
import {
	Autopay,
	ExpirationType,
	AchAccountType,
	Card,
	Ach,
	PaymentMethodType
} from '../../models/autopay';
import { Account } from '../../models/customer';
import { Portfolio } from '../../models/portfolio';
import { AutopayService } from './autopay.service';
import { TemplateData } from '../../models/template-data';
import { FeeCalculationValue } from '../../models/feeCalculationValue';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { PaymentPlanType } from '../../models/paymentPlanType';

@autoinject
export class AutopayConfirmation {
	paymentInformation: any; // TODO: Build class around this
	isLoading = false;
	isSubmitting = false;
	state = 'edit';
	autopay: Autopay;
	account: Account;
	portfolio: Portfolio;
	isChangingPaymentMethod = false;
	validationErrors: string[];
	numberOfRetries = 0;
	today: string; // formatted date
	content: string;
	hashedContent: string;
	totalServiceFee: number;
	paymentDate: moment.Moment;
	transactionError: any;
	makePaymentLabel: string;
	confirmationTemplateData: TemplateData;
	confirmationTitle: string;
	feeValues: FeeCalculationValue[]; // Needed to update tiered fees when payment method changes
	paymentPlanType: PaymentPlanType = PaymentPlanType.Autopay;
	formattedConvenienceFee: string;
	isFeeOverridden: boolean;
	isTieredFees: boolean;
	allowFeesOnAutopays: boolean;
	paymentMethodKey: number;

	constructor(
		private globalState: State,
		private router: Router,
		public i18n: I18N,
		public autopayService: AutopayService
	) {
		this.confirmationTitle = this.i18n.i18next.t('completePlanSetUp', 'Please review the below information, enter the Account Holder Name EXACTLY AS IT APPEARS BELOW, and click "I Accept", in order to complete the plan set-up.');
	}

	activate(model) {
		this.account = this.globalState.accounts.find((element, index, array) => {
			return element.id === model.id;
		});

		this.portfolio = this.globalState.customer.portfolios.find((element, index, array) => {
			return element.portfolioName === this.account.portfolioName;
		});

		if (!this.autopayService.autopay) {
			this.goBack();
		}

		this.autopay = { ...this.autopayService.autopay };
		this.feeValues = this.autopay.feeValues;
		this.formattedConvenienceFee = this.autopayService.formattedConvenienceFee;
		// fee overrides populate the autopay fee value column
		this.isFeeOverridden = this.autopay.feeValue != null;
		// if overridden, that fee value is always used in portal
		this.isTieredFees = !this.isFeeOverridden && this.autopayService.getIsTieredFees();
		this.allowFeesOnAutopays = this.portfolio.merchantSettings.feeSettings.allowFeesOnAutopays;
		this.paymentMethodKey = this.autopay.paymentMethod ? this.autopay.paymentMethod.paymentMethodKey : null;
	}

	get paymentMethod() {
		if (!this.paymentInformation) {
			return this.autopay.paymentMethod.accountNumber;
		}

		if (this.paymentInformation.isWallet) {
			if (this.paymentInformation.card && this.paymentInformation.card.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.card.selectedPaymentMethod.cardType} ending ${this.paymentInformation.card.selectedPaymentMethod.last4ofCard}`, paymentMethodTypes: this.paymentInformation.card.selectedPaymentMethod.cardType, accountNumber: this.paymentInformation.card.selectedPaymentMethod.last4ofCard });
			}
			else if (this.paymentInformation.check && this.paymentInformation.check.selectedPaymentMethod) {
				return this.i18n.i18next.t('walletPaymentMethods', { defaultValue: `${this.paymentInformation.check.selectedPaymentMethod.achAccountType} ending ${this.paymentInformation.check.selectedPaymentMethod.last4ofAccount}`, paymentMethodTypes: this.paymentInformation.check.selectedPaymentMethod.achAccountType, accountNumber: this.paymentInformation.check.selectedPaymentMethod.last4ofAccount });
			}
		}
		else {
			if (this.paymentInformation.isCardPayment){
				return this.paymentInformation.card.description;
			}
			if (this.paymentInformation.isCheckPayment){
				return this.paymentInformation.check.description;
			}
		}
	}

	showConfirmation(event) {
		this.validationErrors = event? event.detail.errors : [];
		this.validate();

		if (this.validationErrors.length === 0) {
			this.state = 'confirm';
			this.paymentInformation = event ? event.detail : null;
			this.confirmationTemplateData = this.getConfirmationTemplateData(this.feeValues);
		}
	}

	validate() {
		this.validationErrors = this.validationErrors || [];
		const isMaxPaymentEmpty = !this.autopay.maxPaymentAmount && !this.autopay.payInFull;
		if (isMaxPaymentEmpty) {
			this.validationErrors.push(this.i18n.i18next.t('maxPaymentAmountRequired','Max payment amount is required'));
		}

		const isDirty =
			this.autopay.maxPaymentAmount !== this.autopayService.autopay.maxPaymentAmount ||
			this.autopay.payInFull !== this.autopayService.autopay.payInFull;
		if (!this.isChangingPaymentMethod && !isDirty) {
			this.validationErrors.push(this.i18n.i18next.t('noChangesHaveBeenMade','No changes have been made'));
		}
	}

	getConfirmationTemplateData(feeValues: FeeCalculationValue[]) {
		this.totalServiceFee = 0;

		let endDate = this.autopay.endDate || 'N/A';
		if (!this.autopay.numberOfPayments && !this.autopay.endDate) {
			endDate = 'Indefinite';
		}

		let customObject: any = {
			paymentAmountLimit: numeral(this.autopay.maxPaymentAmount).format('($0,0.00)'),
			payInFull: this.autopay.payInFull,
			accountNumberLabel: this.globalState.business.portalSettings.accountNumberLabel,
			customerAccountNumber: this.account.referenceId,
			accountNumber: this.account.referenceId, // backwards comaptbility
			customerAccountName: this.account.name,
			balance: numeral(this.account.balance).format('($0,0.00)'),
			serviceFee: this.formattedConvenienceFee,
			planFrequency: this.autopay.frequency.description,
			planStartDate: moment(this.autopay.startDate).format('MM/DD/YYYY'),
			planEndDate: endDate !== 'Indefinite' ? moment(endDate).format('MM/DD/YYYY') : endDate,
			numberOfPayments: this.autopay.numberOfPayments || 'N/A',
			merchantName: this.globalState.business.name,
			paymentScheduleTable: '',
			accountName: this.paymentInformation ? this.paymentInformation.name : this.autopay.paymentMethod.accountName,
			zipCode: this.paymentInformation ? this.paymentInformation.zipCode : this.autopay.paymentMethod.zipCode,
			paymentMethod: this.paymentMethod,
			customerAccountLocationName: (this.account.bucket) ? this.account.bucket.displayName : '',
			customerAccountLocationReferenceId: (this.account.bucket) ? this.account.bucket.referenceId : '',
			billingPhone: this.globalState.customer.mobilePhone,
			billingEmail: this.globalState.customer.email
		};

		if (this.isFeeOverridden) {
			feeValues = null;
		}

		const merchantKey = this.globalState.business.getMerchantKeyAssociatedToPortfolioByPortfolioName(this.account.portfolioName);
		return new TemplateData(customObject, merchantKey, null, feeValues);
	}

	getFormattedConvenienceFee(feeCalculationValue: FeeCalculationValue) {
		return feeCalculationValue.feeCalculationType === FeeCalculationType.Percentage
			? `${numeral(feeCalculationValue.feeValue).format('0.00')}%`
			: numeral(feeCalculationValue.feeValue).format('($0,0.00)');
	}

	getAutopayPaymentMethod(): Card|Ach {
		let method: Card|Ach = null;
		if (!this.paymentInformation) {
			method = this.autopay.paymentMethod;
		} else {
			let paymentDetails = this.paymentInformation.isCardPayment ? this.paymentInformation.card : this.paymentInformation.check;
			let wallet = paymentDetails.selectedPaymentMethod;

			if (this.paymentInformation.isCardPayment) {
				method = new Card();
				method.paymentMethodType = PaymentMethodType.Card;
				if (wallet) {
					method.paymentMethodKey = wallet.cardInfoKey;
					this.autopayService.savedToWallet = false;
				} else {
					method.accountNumber = paymentDetails.cardNumber;
					method.expirationMonth = paymentDetails.expMonth;
					method.expirationYear = paymentDetails.expYear;
					method.cvv = paymentDetails.cvv;
					this.autopayService.savedToWallet = true;
				}
			} else {
				method = new Ach();
				method.paymentMethodType = PaymentMethodType.ECheck;
				if (wallet) {
					method.paymentMethodKey = wallet.checkInfoKey;
				} else {
					method.accountNumber = paymentDetails.accountNumber;
					method.aba = paymentDetails.transitNumber;

					if (paymentDetails.accountType === 'checking') {
						method.achAccountType = AchAccountType.checking;
					} else if (paymentDetails.accountType === 'savings') {
						method.achAccountType = AchAccountType.savings;
					} else {
						method.achAccountType = AchAccountType.businessChecking;
					}
				}
			}

			method.isThirdParty = this.paymentInformation.isThirdPartyPayment;
			method.accountName = this.paymentInformation.name;
			method.city = this.paymentInformation.billingCity;
			method.state = this.paymentInformation.billingState;
			method.customerKey = this.globalState.customer.customerKey;
			method.isPrimaryPaymentMethod = true;
			method.zipCode = this.paymentInformation.zipCode;
		}

		return method;
	}

	showError(message: string) {
		alert(message); // TODO: use something other than alerts
	}

	submit() {
		const method = this.getAutopayPaymentMethod();
		this.autopayService.updateAutopay(this.autopay.key, method, this.autopay.maxPaymentAmount, this.autopay.payInFull)
			.then((result) => {
				this.isSubmitting = false;
				if (result.isSuccessful) {
					this.autopayService.autopay.key = result.value.autopayKey;
					this.goToReceipt();
				}
			})
			.catch((error) => {
				this.isSubmitting = false;
				this.showError(this.i18n.i18next.t('anErrorOccurred', 'An Error Occurred'));
			});

	}

	goToReceipt() {
		this.router.navigateToRoute('autopay/autopay-receipt', { id: this.account.id });
	}

	goBack() {
		if (this.state === 'edit') {
			this.router.navigateToRoute('autopay/view-autopay', { id: this.account.id });
		} else if (this.state === 'confirm') {
			this.content = '';
			this.state = 'edit';
		}
	}
}
